import { useState, useEffect, useMemo } from "react";
import { Search, ArrowUpDown, ExternalLink, Users, Clock } from "lucide-react";
import { Shimmer, CardGridShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { getSharedSubscriptions, type SharedSubscription } from "@/utils/shared-subscriptions-api";

type SortOption = "discount-high" | "discount-low" | "price-high" | "price-low" | "name-asc";

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "discount-high", label: "Highest Discount" },
  { value: "discount-low", label: "Lowest Discount" },
  { value: "price-low", label: "Lowest Price" },
  { value: "price-high", label: "Highest Price" },
  { value: "name-asc", label: "Name A-Z" },
];

function formatDuration(d: number, type: string): string {
  if (d === 1 && type === "months") return "Monthly";
  if (d === 3 && type === "months") return "Quarterly";
  if (d === 1 && type === "years") return "Yearly";
  return `${d} ${type}`;
}

function getDiscountBadgeVariant(d: number): "default" | "secondary" | "outline" {
  if (d >= 60) return "default";
  if (d >= 40) return "secondary";
  if (d > 0) return "outline";
  return "secondary";
}

export default function SharedSubscriptionsDiscountPage() {
  const { userId, token } = useAuth();

  const [plans, setPlans] = useState<SharedSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const perPage = 12;
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortOption>("discount-high");
  const [durationFilter, setDurationFilter] = useState("");

  useEffect(() => {
    if (!userId || !token) return;
    setLoading(true);
    getSharedSubscriptions(userId, token)
      .then(setPlans)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load subscriptions"))
      .finally(() => setLoading(false));
  }, [userId, token]);

  const filtered = useMemo(() => {
    let result = plans;

    if (durationFilter) {
      if (durationFilter === "monthly") {
        result = result.filter((p) => p.duration === 1 && p.durationType === "months");
      } else if (durationFilter === "quarterly") {
        result = result.filter((p) => p.duration === 3 && p.durationType === "months");
      } else if (durationFilter === "yearly") {
        result = result.filter((p) => p.duration === 1 && p.durationType === "years");
      }
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q));
    }

    result = [...result].sort((a, b) => {
      switch (sort) {
        case "discount-high": return b.discountPercentage - a.discountPercentage;
        case "discount-low": return a.discountPercentage - b.discountPercentage;
        case "price-low": return a.sharedPrice - b.sharedPrice;
        case "price-high": return b.sharedPrice - a.sharedPrice;
        case "name-asc": return a.name.localeCompare(b.name);
      }
    });

    return result;
  }, [plans, search, sort]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => { setPage(1); }, [search, sort, durationFilter]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-1">
            Shared Subscriptions
          </h1>
          <p className="text-muted-foreground">
            Browse shared subscription plans and their discount rates.
          </p>
        </div>
        <a href="https://docs.superflow.run/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-sm font-medium hover:bg-muted transition-colors">
          API Docs <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <span className="text-sm text-muted-foreground font-medium">
          {filtered.length} plan{filtered.length !== 1 ? "s" : ""} available
        </span>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search plans..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[220px]"
            />
          </div>
          <Select value={durationFilter || "ALL"} onValueChange={(v) => setDurationFilter(!v || v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
            <SelectTrigger className="w-[180px]">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Shimmer loading={loading} fallback={<CardGridShimmer />}>
        {paginated.length === 0 ? (
          <div className="flex justify-center py-20">
            <p className="text-muted-foreground">
              {search ? "No plans match your search" : "No plans available"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {paginated.map((plan) => (
                <Card
                  key={plan.planId}
                  className="h-full transition-all duration-200 hover:border-primary hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <CardContent className="p-5 flex flex-col h-full">
                    {/* Logo + Discount badge */}
                    <div className="flex items-start justify-between mb-4">
                      <Avatar className="h-12 w-12 rounded-lg border">
                        <AvatarImage src={plan.dp} alt={plan.name} />
                        <AvatarFallback className="rounded-lg">{plan.name[0]}</AvatarFallback>
                      </Avatar>
                      <Badge variant={getDiscountBadgeVariant(plan.discountPercentage)}>
                        {plan.discountPercentage}% off
                      </Badge>
                    </div>

                    {/* Plan name */}
                    <span className="text-sm font-bold mb-2">
                      {plan.name.trim()}
                    </span>

                    {/* Meta chips */}
                    <div className="flex gap-1.5 flex-wrap mb-3">
                      <Badge variant="outline" className="text-[0.7rem]">
                        <Clock className="h-3 w-3" />
                        {formatDuration(plan.duration, plan.durationType)}
                      </Badge>
                      <Badge variant="outline" className="text-[0.7rem]">
                        <Users className="h-3 w-3" />
                        {plan.shareLimit} slots
                      </Badge>
                    </div>

                    {/* Pricing */}
                    <div className="flex-1" />
                    <div className="flex flex-col gap-1 mt-2 pt-3 border-t">
                      <div className="flex justify-between">
                        <span className="text-xs text-muted-foreground">Plan Price</span>
                        <span className="text-xs font-semibold">{"\u20B9"}{plan.planPrice}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-muted-foreground">Per Slot</span>
                        <span className="text-sm font-bold text-primary">
                          {"\u20B9"}{plan.sharedPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </Shimmer>
    </div>
  );
}
